/*
  Rating histogram for the PDP review rail (design 03 Product, c2 · Reviews).

  Judge.me's minimal-header widget renders an average and a count and no
  distribution — but the distribution is in the page anyway. Its loader
  publishes a per-product payload at:

      jdgm.data.reviewWidget[<productId>].histogram
      -> [{ rating: 5, frequency: 21, percentage: 81 }, ...]

  and `widget_show_histogram` is already true on the account, so this is data
  the store is entitled to draw; only the header variant that would render it is
  switched off. Rather than require that setting, the rows are rendered here.

  COUPLING, stated plainly: `jdgm.data` is Judge.me's internal shape, not a
  documented API, and it can change without notice. Everything below fails
  closed — if the payload is missing or malformed the rail simply has no
  histogram, exactly as it does today. It never throws and never blocks the
  widget. If Judge.me's fuller header is ever switched on, delete this file and
  its stylesheet block: the app will draw its own.
*/
(function () {
  'use strict';

  var WIDGET_ID = 'judgeme_product_reviews';
  var MAX_WAIT_MS = 15000;
  var POLL_MS = 250;

  function histogramFor(widget) {
    var data = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
    if (!data) return null;

    // The payload is keyed by product id. Prefer the id the widget itself
    // carries; fall back to the only entry when there is exactly one, which
    // covers a PDP rendering a single product.
    var id = widget.getAttribute('data-product-id');
    var entry = (id && data[id]) || null;

    if (!entry) {
      var keys = Object.keys(data);
      if (keys.length === 1) entry = data[keys[0]];
    }

    var rows = entry && entry.histogram;
    if (!Array.isArray(rows) || !rows.length) return null;

    // Normalise and sort 5 -> 1. Judge.me has been consistent about ordering,
    // but the design depends on it, so it is enforced rather than assumed.
    var clean = rows
      .map(function (row) {
        return {
          rating: Number(row.rating),
          frequency: Number(row.frequency) || 0,
          percentage: Math.max(0, Math.min(100, Number(row.percentage) || 0)),
        };
      })
      .filter(function (row) {
        return row.rating >= 1 && row.rating <= 5;
      })
      .sort(function (a, b) {
        return b.rating - a.rating;
      });

    if (!clean.length) return null;
    return { rows: clean, average: Number(entry.average_rating) || 0 };
  }

  var STAR_PATH =
    'M12 2.5l2.9 6 6.6 0.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-0.9z';

  /* The design sets five stars beside the average, the remainder drawn at 35%.
     Judge.me's minimal header prints the number alone, so the row is built here
     from the same payload. Marked aria-hidden: the average is already read out
     as text immediately before it. */
  function buildStars(average) {
    var filled = Math.round(average);
    var row = document.createElement('span');
    row.className = 'kashira-avg-stars';
    row.setAttribute('aria-hidden', 'true');

    for (var i = 1; i <= 5; i += 1) {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('focusable', 'false');
      if (i > filled) svg.setAttribute('class', 'kashira-avg-stars__muted');

      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', STAR_PATH);
      svg.appendChild(path);
      row.appendChild(svg);
    }

    return row;
  }

  function renderStars(widget, average) {
    var display = widget.querySelector('.jm-average-rating-display');
    if (!display || display.querySelector('.kashira-avg-stars')) return;

    var cluster = display.querySelector('.jm-cluster') || display;
    var number = cluster.firstElementChild;
    if (!number) return;

    cluster.insertBefore(buildStars(average), number.nextSibling);
    cluster.classList.add('kashira-avg-cluster');
  }

  function build(rows) {
    var total = rows.reduce(function (sum, row) {
      return sum + row.frequency;
    }, 0);

    var wrap = document.createElement('div');
    wrap.className = 'kashira-histogram';
    // A bar chart is decorative to a screen reader; the same numbers are read
    // out here as one sentence instead of ten unlabelled cells.
    wrap.setAttribute('role', 'img');
    wrap.setAttribute(
      'aria-label',
      rows
        .map(function (row) {
          return row.frequency + ' of ' + total + ' reviews rated ' + row.rating + ' out of 5';
        })
        .join('. ')
    );

    rows.forEach(function (row) {
      var line = document.createElement('div');
      line.className = 'kashira-histogram__row';

      var label = document.createElement('span');
      label.className = 'kashira-histogram__label';
      label.textContent = row.rating;

      var track = document.createElement('span');
      track.className = 'kashira-histogram__track';

      var fill = document.createElement('span');
      fill.className = 'kashira-histogram__fill';
      fill.style.setProperty('--kashira-histogram-pct', row.percentage + '%');

      var count = document.createElement('span');
      count.className = 'kashira-histogram__count';
      count.textContent = row.frequency;

      track.appendChild(fill);
      line.appendChild(label);
      line.appendChild(track);
      line.appendChild(count);
      wrap.appendChild(line);
    });

    return wrap;
  }

  /* The handoff fills the bars at 30% of the viewport. Dawn's own reveals use
     IntersectionObserver, so this matches rather than inventing a second
     mechanism. Reduced motion skips straight to the filled state. */
  function reveal(wrap) {
    var calm =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (calm || typeof IntersectionObserver === 'undefined') {
      wrap.classList.add('kashira-histogram--filled');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('kashira-histogram--filled');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(wrap);
  }

  /* Judge.me draws an empty star as a DIFFERENT GLYPH — U+E000 is the filled
     star, U+E001 the outline — while the design fades the filled shape to 35%
     instead and never uses an outline. All five spans carry the same class, so
     there is nothing for CSS to select on; the empties are tagged here.

     The text node is deliberately left alone. Rewriting it would put us in a
     fight with Judge.me's renderer over the same character; a class plus an
     overlaid pseudo-element leaves their DOM as they wrote it. */
  var EMPTY_STAR = '';

  function normaliseStars(root) {
    if (!root) return;
    var stars = root.querySelectorAll('.jm-star-rating__font-icon');
    for (var i = 0; i < stars.length; i += 1) {
      var span = stars[i];
      var empty = (span.textContent || '').indexOf(EMPTY_STAR) !== -1;
      span.classList.toggle('kashira-star--empty', empty);
    }
  }

  /* Judge.me prints review dates US-first — its account setting
     `review_date_format` is "mm/dd/yyyy", so 13 August renders as 08/13/2026
     while every other date on this India-first storefront reads day-first.

     THE REAL FIX IS THAT SETTING, in Judge.me admin. This is a stopgap, and it
     is written so the two can never both apply: the guard below reads the same
     setting, so the moment it is corrected this becomes a no-op rather than
     swapping an already-correct date back to front. Delete it once the account
     is right. Swapping is safe rather than a guess precisely because the source
     order is known from the setting. */
  var US_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  function normaliseDates(root) {
    if (!root) return;
    var settings = window.jdgmSettings;
    if (!settings || settings.review_date_format !== 'mm/dd/yyyy') return;

    var nodes = root.querySelectorAll('.jm-reviewer-info__details span, .jm-review-item time');
    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      if (node.children.length) continue;
      // The swap MUST be idempotent. 13/08/2026 matches the same pattern as
      // 08/13/2026, so without this marker a second pass flips it back — and
      // because writing textContent is itself a mutation, the observer below
      // re-entered and the two swapped forever, freezing the renderer.
      if (node.getAttribute('data-kashira-date') === '1') continue;
      var text = (node.textContent || '').trim();
      var parts = US_DATE.exec(text);
      if (!parts) continue;
      node.textContent = parts[2] + '/' + parts[1] + '/' + parts[3];
      node.setAttribute('data-kashira-date', '1');
    }
  }

  function normaliseAll() {
    var widget = document.getElementById(WIDGET_ID);
    var lightbox = document.querySelector('.jdgm-modal');
    normaliseStars(widget);
    normaliseStars(lightbox);
    // The photo lightbox renders at body level, outside the widget.
    normaliseDates(widget);
    normaliseDates(lightbox);
  }

  function render() {
    var widget = document.getElementById(WIDGET_ID);
    if (!widget) return false;
    normaliseStars(widget);
    normaliseDates(widget);
    if (widget.querySelector('.kashira-histogram')) return true;

    // Judge.me draws its own histogram in the non-minimal header. If that is
    // ever switched on, stand down rather than showing two.
    //
    // This match is deliberately exact. An earlier version used
    // [class*="histogram"], which matched something transient during hydration
    // and permanently stopped the poll — the rail then never got a histogram at
    // all, with no error to show for it.
    if (widget.querySelector('.jm-histogram')) return true;

    var average = widget.querySelector('.jm-average-rating-display');
    if (!average) return false;

    var data = histogramFor(widget);
    if (!data) return false;

    renderStars(widget, data.average);

    var wrap = build(data.rows);
    // The rail stacks average -> count -> histogram -> CTA. The average sits in
    // a .jm-cluster, which is a flex ROW — inserting as its sibling puts the
    // bars beside the number and collapses the tracks to nothing. Climb to the
    // nearest stack (Judge.me's column primitive) and append there instead.
    var stack = average.closest ? average.closest('.jm-stack') : null;
    (stack || average.parentNode).appendChild(wrap);
    reveal(wrap);
    return true;
  }

  function start() {
    if (!document.getElementById(WIDGET_ID)) return;
    if (render()) return;

    // The widget hydrates asynchronously and re-renders on sort and filter, so
    // a one-shot attempt is not enough. Poll briefly, then keep an observer for
    // re-renders that drop the node.
    var waited = 0;
    var timer = setInterval(function () {
      waited += POLL_MS;
      if (render() || waited >= MAX_WAIT_MS) clearInterval(timer);
    }, POLL_MS);

    var widget = document.getElementById(WIDGET_ID);
    // Judge.me re-renders on sort, filter and paging, which drops the star
    // classes and restores its own dates as well as the histogram. This work
    // writes to the DOM, so the observer is disconnected across it — belt and
    // braces alongside the idempotence marker above.
    var busy = false;
    var observer = new MutationObserver(function () {
      if (busy) return;
      busy = true;
      observer.disconnect();
      try {
        normaliseAll();
        if (!widget.querySelector('.kashira-histogram')) render();
      } finally {
        observer.observe(widget, { childList: true, subtree: true });
        busy = false;
      }
    });
    observer.observe(widget, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
